import { useTranslation } from 'react-i18next';
import { firstName, formatMoney, saleDateText } from './LeadClose.functions';
import { RESULT_ICON } from './LeadCloseIcons.component';
import type { tCloseResult } from './LeadClose.types';
import './LeadCloseBubble.styles.scss';

function time(stamp: string): string {
  const instant = new Date(
    stamp.includes('T') ? stamp : `${stamp.replace(' ', 'T')}Z`,
  );
  return instant.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * A result in the conversation (U14): a card apart from the bubbles, tinted
 * with the DS primary accent as the prototype does. It speaks to whoever
 * registered it ("Registraste…") and names the author for everyone else. Only
 * the team sees it: it is drawn from the close summary, never sent to the lead.
 */
export default function LeadCloseBubble(props: {
  result: tCloseResult;
  mine: boolean;
}): JSX.Element {
  const { result, mine } = props;
  const { t } = useTranslation('chat/chat');
  const Icon = RESULT_ICON[result.type];
  const amount = formatMoney(result.amount ?? '0');
  const head = mine
    ? t(`leadClose.card.mine.${result.type}`, { amount })
    : t(`leadClose.card.other.${result.type}`, {
        amount,
        name: firstName(result.actor_name ?? '') || t('leadClose.card.someone'),
      });
  const reason = result.option?.label ?? result.other_text;
  const title =
    result.type === 'sold'
      ? result.what_sold
      : reason
        ? t(`leadClose.card.reason.${result.type}`, { reason })
        : null;

  return (
    <div className="LeadCloseBubble">
      <div
        className="LeadCloseBubble__card ph-no-capture"
        data-testid={`lead-close-card-${result.uuid}`}
      >
        <div className="LeadCloseBubble__head">
          <Icon className="LeadCloseBubble__icon" aria-hidden />
          <span className="LeadCloseBubble__headText">{head}</span>
          <span className="LeadCloseBubble__time">
            {time(result.created_at)}
          </span>
        </div>
        {title ? <div className="LeadCloseBubble__title">{title}</div> : null}
        {result.type === 'sold' && result.sale_date ? (
          <div className="LeadCloseBubble__meta">
            {t('leadClose.card.saleDate', {
              date: saleDateText(result.sale_date),
            })}
          </div>
        ) : null}
        {result.note ? (
          <div className="LeadCloseBubble__desc">{result.note}</div>
        ) : null}
      </div>
    </div>
  );
}
